const { useTranslation, withTranslation } = require('react-i18next');
const { useState, useEffect, useMemo } = require('react');
const { connect, useDispatch } = require('react-redux');
var distributionActions = require('distributionActions');
const React = require('react');
const searchIcon = require('images/icons/search_icon.svg');
const { SelectMenu } = require('SelectMenu');
var sensorsManager = require('sensorsManager');
var ChipButton = require('ChipButton');
var distributionManager = require('@managers/distributionManager');
var Spinner = require('../general/Spinner.jsx');
const {
  areAllDistributionTreesLoaded,
  TREE_LOADING_TIMEOUT_MS,
} = require('./transmitterTestHelpers');

const ByFieldSearch = ({
  all_distributions,
  all_distribution_load_status,
  onTestClick,
  loading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const distributionsMap = all_distributions?.distributions;

  // Keyed on the map, not mount-only: ensure_all_distributions_loaded early-
  // returns when it finds nothing to hydrate, and it reads the map as
  // `distributions?.distributions || {}` — so on a cold page load, when the map
  // is still null, a mount-only dispatch no-ops and nothing is ever hydrated.
  // It dedups in-flight sweeps and skips trees that are already full and fresh,
  // so re-running it as the map changes is cheap.
  useEffect(() => {
    dispatch(distributionActions.ensure_all_distributions_loaded()).catch(
      (err) =>
        console.error(
          '[TransmitterTest] hydrating distributions failed:',
          err?.message,
        ),
    );
  }, [dispatch, distributionsMap]);

  // Gate on the DATA SHAPE, not on the sweep promise: the promise resolves early
  // when there is nothing to hydrate (and the bulk route can partially fail), so
  // it says nothing about whether the dropdowns have anything to show. Mirrors
  // the field/farm view gate (Dashboard.jsx, IrrigationSystemMonitoring.jsx),
  // widened from the selected tree to ALL of them because this screen lets an
  // admin pick any distribution.
  //
  // A failed load is the escape hatch — nothing further is coming, so spinning
  // forever helps no one. That also covers an account with no distributions.
  const isTreeIncomplete =
    all_distribution_load_status?.status !== 'fail' &&
    !areAllDistributionTreesLoaded(
      distributionsMap,
      distributionManager.isFullDistributionTree,
    );

  // The sweep swallows its own failures, so "incomplete" alone could block the
  // screen forever. Time-box it and fall back to showing whatever arrived.
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
  useEffect(() => {
    if (!isTreeIncomplete) {
      return undefined;
    }
    const timeoutId = setTimeout(
      () => setHydrationTimedOut(true),
      TREE_LOADING_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [isTreeIncomplete]);

  const isTreeLoading = isTreeIncomplete && !hydrationTimedOut;

  const isLoading = loading || isTreeLoading;
  const [selectedDistribution, setSelectedDistribution] = useState(undefined);
  const [selectedFarmingUnit, setSelectedFarmingUnit] = useState(undefined);
  const [selectedField, setSelectedField] = useState(undefined);
  const [selectedSensor, setSelectedSensor] = useState(undefined);
  const [distributionsMenu, setDistributionsMenu] = useState([]);
  const [farmingUnitsMenu, setFarmingUnitsMenu] = useState([]);
  const [fieldsMenu, setFieldsMenu] = useState([]);
  let showSensorsLabel = false;
  if (
    selectedField?.sensors !== undefined &&
    Object.keys(selectedField?.sensors).length > 0
  ) {
    showSensorsLabel = true;
  }

  useEffect(() => {
    if (
      selectedDistribution !== undefined ||
      isTreeLoading ||
      !all_distributions
    ) {
      return;
    }
    let sortedDistributions = Object.values(all_distributions.distributions);
    if (!sortedDistributions.length) {
      return;
    }
    sortedDistributions.sort((a, b) => a.name.localeCompare(b.name));
    const firstDist = sortedDistributions[0];
    const firstFu = Object.values(firstDist.farming_units || {}).sort((a, b) =>
      a.name.localeCompare(b.name),
    )[0];
    if (!firstFu?.fields) {
      return;
    }
    setDistributionsMenu(
      sortedDistributions.map((dist) => ({
        title: dist.name,
        value: dist.name,
      })),
    );
    setSelectedDistribution(firstDist);
    const sortedFarmingUnits = Object.values(firstDist.farming_units).sort(
      (a, b) => a.name.localeCompare(b.name),
    );
    setFarmingUnitsMenu(
      sortedFarmingUnits.map((farmingUnit) => ({
        title: farmingUnit.name,
        value: farmingUnit.name,
      })),
    );
    setSelectedFarmingUnit(sortedFarmingUnits[0]);
    const updatedFields = Object.values(sortedFarmingUnits[0].fields)
      .filter((field) => field.settings.field_state === 'active')
      .sort((a, b) => a.name.localeCompare(b.name));
    setFieldsMenu(
      updatedFields.map((field) => ({ title: field.name, value: field.id })),
    );
    setSelectedField(updatedFields[0]);
  }, [all_distributions, selectedDistribution, isTreeLoading]);

  const onChangeDistribution = (distributionName) => {
    let distributions = Object.values(all_distributions.distributions);
    let distributionObj = distributions.filter((distribution) => {
      if (distribution.name === distributionName) return distribution;
    });
    setSelectedDistribution(distributionObj[0]);
    if (Object.keys(distributionObj[0]?.farming_units).length === 0) {
      setFarmingUnitsMenu([]);
      setSelectedFarmingUnit(undefined);
      setFieldsMenu([]);
      setSelectedField(undefined);
      return;
    } else {
      let sortedFarmingUnits = Object.values(distributionObj[0].farming_units);
      sortedFarmingUnits.sort((a, b) => a.name.localeCompare(b.name));
      let sortedFarmingUnitsMenu = sortedFarmingUnits.map((farmingUnit) => {
        return { title: farmingUnit.name, value: farmingUnit.name };
      });
      setFarmingUnitsMenu(sortedFarmingUnitsMenu);
      setSelectedFarmingUnit(sortedFarmingUnits[0]);
      onChangeFarmingUnit(distributionObj[0], sortedFarmingUnits[0].name);
    }
  };

  const onChangeFarmingUnit = (distribution, farmingUnitName) => {
    let distributions = Object.values(all_distributions.distributions);
    let distributionObj = distributions.filter((dist) => {
      if (dist.name === distribution.name) return distribution;
    });
    let farmingUnits = Object.values(distributionObj[0]?.farming_units);
    let farmingUnitObj = farmingUnits.filter((farming_unit) => {
      if (farming_unit.name === farmingUnitName) return farming_unit;
    });
    setSelectedFarmingUnit(farmingUnitObj[0]);
    if (!farmingUnitObj[0]?.fields) {
      setFieldsMenu([]);
      setSelectedField(undefined);
      return;
    }
    let sortedFields = Object.values(farmingUnitObj[0].fields);
    sortedFields.sort((a, b) => a.name.localeCompare(b.name));
    let updatedFields = sortedFields.filter((field) => {
      if (field.settings.field_state === 'active') {
        return field;
      }
    });
    let sortedFieldsMenu = updatedFields.map((field) => {
      return { title: field.name, value: field.id };
    });
    setFieldsMenu(sortedFieldsMenu);
    setSelectedField(updatedFields[0]);
  };

  const onChangeField = (fieldId) => {
    let fields = Object.values(selectedFarmingUnit.fields);
    let newFieldObj = fields.filter((field) => {
      return field.id === fieldId;
    });
    setSelectedField(newFieldObj[0]);
  };

  const onByFieldSensorPressed = (transmitter_id) => {
    onTestClick(transmitter_id.toLowerCase());
  };

  const SensorElements = () => {
    const { t } = useTranslation();
    if (selectedField) {
      let sensorsArr = Object.keys(selectedField.sensors)
        .sort()
        .map((id) => selectedField.sensors[id])
        .filter((sensor) => {
          if (
            sensor?.transmitter_id?.toLowerCase().startsWith('gt') ||
            sensor?.transmitter_id?.toLowerCase().startsWith('mt')
          )
            return sensor;
        });

      let index = 0;
      return sensorsArr.map((sensor) => {
        let sensorColor = '';
        let sensorType = sensor.type.includes('piezometer')
          ? ' (e-piezo)'
          : sensor.type.includes('wfr') && !sensor.type.includes('tal-gil')
            ? ' (Flow Rate)'
            : sensor.type.includes('pressure')
              ? ' (pressure)'
              : '';

        if (sensorType === '') {
          sensorColor = sensorsManager.sensorColorByIndex(
            index,
            selectedField.field_state,
          );
          index++;
        } else if (sensorType === ' (e-piezo)') {
          sensorColor = '#662F6F';
        } else if (sensorType === ' (Flow Rate)') {
          sensorColor = '#3A8500';
        } else if (sensorType === ' (pressure)') {
          sensorColor = '#1565C0';
        }
        const isSelected =
          selectedSensor?.transmitter_id === sensor.transmitter_id;
        const displayName = sensor.transmitter_id + sensorType;

        return (
          <ChipButton
            key={sensor.transmitter_id}
            label={displayName}
            onClick={() => {
              if (!isLoading) {
                setSelectedSensor(sensor);
                onByFieldSensorPressed(sensor.transmitter_id);
              }
            }}
            isSelected={isSelected}
            borderColor={sensorColor}
            backgroundColor={sensorColor}
            textColor={sensorColor}
            style={{
              cursor: isLoading ? 'auto' : 'pointer',
              fontSize: '16px',
            }}
            className={isLoading ? '' : ''}
          />
        );
      });
    } else {
      return null;
    }
  };

  if (isTreeLoading) {
    return (
      <div
        className={'tab-pane fade show active row'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          paddingTop: 8,
          width: '100%',
        }}
      >
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '160px' }}
        >
          <Spinner size='medium' />
        </div>
      </div>
    );
  }

  return (
    <div
      className={'tab-pane fade show active row'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingTop: 8,
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 16 }}>
        <div
          className='text-grey45 col-4 col-sm-4 col-xs-4 col-md-4 col-lg-3'
          style={{
            flexDirection: 'column',
            marginTop: 16,
            width: '23%',
            marginLeft: 0,
          }}
        >
          <span style={{ fontSize: 14 }}>{t('Distribution')}</span>
          <div style={{ marginTop: 8 }}>
            <SelectMenu
              options={distributionsMenu}
              curValue={selectedDistribution?.name}
              sortBox={''}
              onChange={(e) => {
                onChangeDistribution(e);
              }}
            />
          </div>
        </div>
        <div
          className='text-grey45 col-4 col-sm-4 col-xs-4 col-md-4 col-lg-3'
          style={{ flexDirection: 'column', marginTop: 16, width: '23%' }}
        >
          <span style={{ fontSize: 14 }}>{t('farming_unit')}</span>
          <div style={{ marginTop: 8 }}>
            <SelectMenu
              options={farmingUnitsMenu}
              curValue={selectedFarmingUnit?.name}
              onChange={(e) => {
                onChangeFarmingUnit(selectedDistribution, e);
              }}
              sortBox={''}
            />
          </div>
        </div>
        <div
          className='text-grey45 col-4 col-sm-4 col-xs-4 col-md-4 col-lg-3'
          style={{ flexDirection: 'column', marginTop: 16, width: '23%' }}
        >
          <span style={{ fontSize: 14 }}>{t('field')}</span>
          <div style={{ marginTop: 8 }}>
            <SelectMenu
              options={fieldsMenu}
              curValue={selectedField?.id || fieldsMenu[0]}
              onChange={(e) => {
                onChangeField(e);
              }}
              sortBox={''}
            />
          </div>
        </div>
      </div>
      <div style={{ marginLeft: 16 }}>
        {showSensorsLabel ? (
          <span className='text-grey45' style={{ fontSize: 14 }}>
            {t('transmitters')}
          </span>
        ) : null}
        <div style={{ display: 'flex', marginTop: 10, flexWrap: 'wrap' }}>
          <SensorElements />
        </div>
      </div>
    </div>
  );
};

const FreeSearch = ({ onTestClick, initialTransmitterId }) => {
  const { t } = useTranslation();
  const [transmitterID, setTransmitterID] = useState(
    initialTransmitterId || '',
  );
  const inputRef = React.useRef(null);

  // Update transmitterID when initialTransmitterId changes
  useEffect(() => {
    if (initialTransmitterId && initialTransmitterId !== transmitterID) {
      setTransmitterID(initialTransmitterId);
    }
  }, [initialTransmitterId]);

  const onFreeSearchTestPressed = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Local check (given by bootstrap)
    if (event.target.checkValidity() == true) {
      let newTransmitterID = transmitterID.toLowerCase();
      if (
        !newTransmitterID.startsWith('gt_') &&
        !newTransmitterID.startsWith('aq_') &&
        !newTransmitterID.startsWith('mt_')
      ) {
        if (newTransmitterID.length === 3 || newTransmitterID.length === 4) {
          newTransmitterID = 'gt_' + newTransmitterID;
        } else if (newTransmitterID.length === 6) {
          newTransmitterID = 'gt_9180089' + newTransmitterID;
        } else if (newTransmitterID.length === 7) {
          newTransmitterID = 'gt_918008' + newTransmitterID;
        }
      } else if (
        newTransmitterID.startsWith('mt_') &&
        newTransmitterID.length < 7
      ) {
        newTransmitterID =
          'mt_' + ('0000' + newTransmitterID.substring(3)).slice(-4);
      }
      if (transmitterID !== newTransmitterID) {
        setTransmitterID(newTransmitterID);
      }
      onTestClick(newTransmitterID);
    }
  };

  return (
    <form
      key={'free'}
      className='needs-validation'
      noValidate
      id='transmitter-form'
      onSubmit={(e) => {
        onFreeSearchTestPressed(e);
      }}
    >
      <div style={{ backgroundColor: 'white' }}>
        <div className={'tab-pane fade show active'}>
          <div className='form-row' style={{ paddingBottom: 8 }}>
            <div className='col-8 col-md-4 col-lg-4 col-sm-8 col-xs-8'>
              <div className='form-group mt-4 text-grey45'>
                <label
                  style={{ fontSize: 14, fontWeight: 400 }}
                  htmlFor='transmitter_id'
                >
                  {t('transmitter_id')}{' '}
                </label>
                <div
                  style={{
                    margin: 'auto',
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <img
                    style={{ position: 'absolute', right: '12px', top: '27%' }}
                    src={searchIcon}
                  />
                  <input
                    className='form-control'
                    ref={inputRef}
                    style={{ width: '100%' }}
                    type='text'
                    name='transmitter_id'
                    value={transmitterID}
                    onChange={(event) => {
                      setTransmitterID(event.target.value);
                    }}
                    placeholder={'Search'}
                    required
                  />
                </div>
              </div>
            </div>
            <div className='d-flex align-items-end justify-content-center'>
              <div className='form-group mt-3 text-light-blue'>
                <button
                  type='submit'
                  className='btn text-white'
                  disabled={!transmitterID}
                  style={{
                    width: '120px',
                    borderRadius: '3px',
                    marginLeft: 8,
                    backgroundColor: '#4563C7',
                  }}
                >
                  {t('test')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

const SearchContainer = ({
  onTestClick,
  all_distributions,
  all_distribution_load_status,
  loading,
  initialTransmitterId,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const [selectedSearchType, setSelectedSearchType] = useState('free');

  const handleTabChange = (newTab) => {
    setSelectedSearchType(newTab);
    if (onTabChange) {
      onTabChange(newTab);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', padding: 16, borderRadius: 5 }}>
      <ul
        className='nav'
        id='kcTab'
        role='tablist'
        style={{ borderBottom: '1px solid #DCE0E8', paddingRight: '15px' }}
      >
        <li className='nav-item'>
          <a
            className={
              'nav-link font-weight-semi-bold text-size-13 pl-0 ' +
              (selectedSearchType === 'free'
                ? 'text-blue-primary underline '
                : 'text-grey71 ')
            }
            id='freeSearch'
            style={{ letterSpacing: '0.07em' }}
            onClick={() => handleTabChange('free')}
            data-toggle='tab'
            href='#home'
            role='tab'
            aria-controls='home'
          >
            {t('free_search').toUpperCase()}
          </a>
        </li>
        <li className='nav-item'>
          <a
            className={
              'nav-link font-weight-semi-bold text-size-13 ' +
              (selectedSearchType === 'byField'
                ? 'text-blue-primary underline '
                : 'text-grey71 ')
            }
            id='byField'
            style={{ letterSpacing: '0.07em' }}
            onClick={() => handleTabChange('byField')}
            data-toggle='tab'
            href='#byField'
            role='tab'
            aria-controls='profile'
          >
            {t('search_by_field').toUpperCase()}
          </a>
        </li>
      </ul>
      <div className={selectedSearchType === 'free' ? 'd-block' : 'd-none'}>
        <FreeSearch
          onTestClick={onTestClick}
          initialTransmitterId={initialTransmitterId}
        />
      </div>
      <div className={selectedSearchType === 'byField' ? 'd-block' : 'd-none'}>
        <ByFieldSearch
          loading={loading}
          onTestClick={onTestClick}
          all_distributions={all_distributions}
          all_distribution_load_status={all_distribution_load_status}
        />
      </div>
    </div>
  );
};

const mapStateToProps = function (state) {
  return {
    user_details: state.authentication_data.user_details,
    all_distributions: state.distribution_data.distributions,
    all_distribution_load_status:
      state.distribution_data.all_distribution_load_status,
  };
};
const mapDispatchToProps = {};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(SearchContainer));
